import { Inject, Injectable } from '@angular/core';
import { PaymentPreferencesStateService } from '../payment-preferences-state/payment-preferences-state.service';
import { PBS, IsbExperience, PersonalBillingRequestParams, IsbCalculateDownPaymentRequestParams, ECIM, ContentfulMapperService } from '@nationwide/dgs-angular-billing-common';
import { Observable, of, forkJoin, map, mergeMap, timeout, catchError } from 'rxjs';
import { PaymentPreferencesApiHttpService } from './payment-preferences-api.http.service';
import * as returnTypes from './payment-preferences-api-calls.model';
import { HttpCachingService } from '../../../../../shared/service/http-caching-service/http-caching.service';
import { SessionService } from '../../../../../shared/session/session.service';
import { LoggerService } from '../../../../../shared/logger/logger.service';
import { DistributionPartnerRoleInfoService } from '../../../../shared/distribution-partner-role-info/distribution-partner-role-info.service';
import { environment } from '../../../../../../environments/environment';
@Injectable()
export class PaymentPreferencesApiService {

    constructor(
        private paymentPreferencesState: PaymentPreferencesStateService,
        private paymentPreferencesHttpService: PaymentPreferencesApiHttpService,
        private httpCachingService: HttpCachingService,
        private session: SessionService,
        private distributionPartnerRoleInfo: DistributionPartnerRoleInfoService,
        readonly contentfulMapperService: ContentfulMapperService,
        @Inject('logger') private logger: LoggerService
    ) { }

    viewPaymentPreferences(personalBillingRequestParams: PersonalBillingRequestParams, calculateDownPaymentRequestParams: IsbCalculateDownPaymentRequestParams): Observable<returnTypes.ViewPaymentPreferences> {
        return this.initializeSessionBeforeCalls().pipe(
            mergeMap(() =>
                forkJoin({
                    retrieveCustomerAgreement: this.httpCachingService.fetchRetrieveCustomerAgreementResponse(),
                    automaticPayments: this.populateObservable(
                        this.paymentPreferencesState.automaticPayments,
                        this.paymentPreferencesHttpService.fetchAutomaticPayments()),
                    refundMethod: this.populateObservable(
                        this.paymentPreferencesState.refundMethod,
                        this.paymentPreferencesHttpService.fetchSavedRefundMethod()),
                    payPlanEligible: of({}),
                    calculateDownPayment: this.session.payPlanEligibleFromPbsRule === 'true' ? this.populateObservable(
                        this.paymentPreferencesState.calculateDownPayment,
                        this.paymentPreferencesHttpService.calculateDownPayment(calculateDownPaymentRequestParams)) : of({}),
                    bills: this.populateObservable(
                        this.paymentPreferencesState.bills,
                        this.paymentPreferencesHttpService.fetchBills()),
                    zeroAmountDueBalances: this.populateObservable(
                        this.paymentPreferencesState.zeroAmountDueBalances,
                        this.paymentPreferencesHttpService.fetchZeroAmountDueBalances(personalBillingRequestParams)
                    ),
                    relatedPolicies: this.populateObservable(
                        this.paymentPreferencesState.relatedPolicies,
                        this.paymentPreferencesHttpService.fetchRelatedPolicies(personalBillingRequestParams)
                    ),
                    eligiblePayPlan: this.populateObservable(
                        this.paymentPreferencesState.eligiblePayPlan,
                        this.paymentPreferencesHttpService.retrieveEligiblePayPlan()
                    ),
                    dpim: this.distributionPartnerRoleInfo.fetchDistributionPartnerRoleInfo(),
                    paymentsHistory: this.populateObservable(
                        this.paymentPreferencesState.paymentsHistory,
                        this.paymentPreferencesHttpService.fetchPaymentHistory().pipe(
                            timeout(environment.TIMEOUT.apiCallDuration.paymentHistory),
                            catchError((error) => {
                                this.logger.error('PAYMENT HISTORY API ERROR: VIEW PAYMENT PREFERENCES FALLBACK TO NULL', { error });
                                return of(null);
                            })
                        )),
                    smartMilesEnrollment: this.populateObservable(
                        this.paymentPreferencesState.smartMilesEnrollment,
                        this.paymentPreferencesHttpService.fetchSmartMilesEnrollment())
                })
            ));
    }

    editPaymentPlanLanding(personalBillingRequestParams: PersonalBillingRequestParams): Observable<returnTypes.EditPaymentPlan> {
        return this.initializeSessionBeforeCalls().pipe(
            mergeMap(() =>
                forkJoin({
                    retrieveCustomerAgreement: this.httpCachingService.fetchRetrieveCustomerAgreementResponse(),
                    bills: this.populateObservable(
                        this.paymentPreferencesState.bills,
                        this.paymentPreferencesHttpService.fetchBills()),
                    relatedPolicies: this.populateObservable(
                        this.paymentPreferencesState.relatedPolicies,
                        this.paymentPreferencesHttpService.fetchRelatedPolicies(personalBillingRequestParams)
                    )
                })
            ));
    }

    createPaymentPreferencesLanding(): Observable<returnTypes.CreatePaymentPreferencesLanding> {
        return this.initializeSessionBeforeCalls().pipe(
            mergeMap(() =>
                forkJoin({
                    retrieveCustomerAgreement: this.httpCachingService.fetchRetrieveCustomerAgreementResponse(),
                    savedPaymentMethods: this.populateObservable(
                        this.paymentPreferencesState.savedPaymentMethods,
                        this.paymentPreferencesHttpService.fetchSavedPaymentMethods()),
                    internetRegistrationResponse: this.retrieveInternetRegistrationResponse(),
                    automaticPayments: this.populateObservable(
                        this.paymentPreferencesState.automaticPayments,
                        this.paymentPreferencesHttpService.fetchAutomaticPayments())
                })
            ));
    }

    editAutomaticPaymentLanding({ callContentful = false }): Observable<returnTypes.EditAutomaticPaymentPaymentLanding> {
        return this.initializeSessionBeforeCalls().pipe(
            mergeMap(() =>
                forkJoin({
                    retrieveCustomerAgreement: this.httpCachingService.fetchRetrieveCustomerAgreementResponse(),
                    automaticPayments: this.populateObservable(
                        this.paymentPreferencesState.automaticPayments,
                        this.paymentPreferencesHttpService.fetchAutomaticPayments()
                    ),
                    internetRegistrationResponse: this.retrieveInternetRegistrationResponse(),
                    paymentsHistory: this.populateObservable(
                        this.paymentPreferencesState.paymentsHistory,
                        this.paymentPreferencesHttpService.fetchPaymentHistory().pipe(
                            timeout(environment.TIMEOUT.apiCallDuration.paymentHistory),
                            catchError((error) => {
                                this.logger.error('PAYMENT HISTORY API ERROR: EDIT AUTOMATIC PAYMENT LANDING FALLBACK TO NULL', { error });
                                return of(null);
                            })
                        )
                    ),
                    contentful: callContentful ? this.contentfulMapperService.getContentfulBillingContent(environment.PRODUCTION) : of(undefined)
                })
            ));
    }

    retrieveInternetRegistrationResponse(): Observable<ECIM.EnterpriseCustomerInformationManagementResponse> {
        return this.populateObservable(
            this.paymentPreferencesState.internetRegistrationResponse,
            this.paymentPreferencesHttpService.fetchInternetRegistrationResponse());
    }

    getSingleSavedPaymentMethod(savedPaymentMethodID?: string): Observable<IsbExperience.SavedPaymentMethod> {
        const payMethodId = savedPaymentMethodID || this.paymentPreferencesState.currentPaymentMethodId;
        return this.initializeSessionBeforeCalls().pipe(
            mergeMap(() =>
                this.populateObservable(
                    this.paymentPreferencesState.getSingleSavedPaymentMethod(payMethodId),
                    this.paymentPreferencesHttpService.fetchSingleSavedPaymentMethod(payMethodId)
                )
            ));
    }

    managePaymentPreferencesBase(): Observable<PBS.RetriveCustomerAgreementResponse> {
        return this.httpCachingService.fetchRetrieveCustomerAgreementResponse();
    }

    cancelFutureSchedulePaymentsLanding(): Observable<returnTypes.CancelFutureSchedulePaymentsLanding> {
        return this.initializeSessionBeforeCalls().pipe(
            mergeMap(() =>
                forkJoin({
                    retrieveCustomerAgreement: this.httpCachingService.fetchRetrieveCustomerAgreementResponse(),
                    paymentsHistory: this.populateObservable(
                        this.paymentPreferencesState.paymentsHistory,
                        this.paymentPreferencesHttpService.fetchPaymentHistory().pipe(
                            timeout(environment.TIMEOUT.apiCallDuration.paymentHistory),
                            catchError((error) => {
                                this.logger.error('PAYMENT HISTORY API ERROR: CANCEL FUTURE SCHEDULED PAYMENTS FALLBACK TO NULL', { error });
                                return of(null);
                            })
                        ))
                })
            ));
    }

    cancelAndSuspendAutomaticPaymentLanding(): Observable<returnTypes.CancelAndSuspendAutomaticPaymentLanding> {
        const personalBillingRequestParams: PersonalBillingRequestParams = {
            accountNumber: this.session.billingAccountNumber,
            accessToken: this.session.accessToken,
            isZeroAmountDueServiceEnable: environment.ZERO_AMOUNT_DUE_API_SERVICE.isEnabled
        };

        return this.initializeSessionBeforeCalls().pipe(
            mergeMap(() =>
                forkJoin({
                    retrieveCustomerAgreement: this.httpCachingService.fetchRetrieveCustomerAgreementResponse(),
                    automaticPayments: this.populateObservable(
                        this.paymentPreferencesState.automaticPayments,
                        this.paymentPreferencesHttpService.fetchAutomaticPayments()),
                    internetRegistrationResponse: this.retrieveInternetRegistrationResponse(),
                    zeroAmountDueBalances: this.populateObservable(
                        this.paymentPreferencesState.zeroAmountDueBalances,
                        this.paymentPreferencesHttpService.fetchZeroAmountDueBalances(personalBillingRequestParams)
                    ),
                    paymentsHistory: this.populateObservable(
                        this.paymentPreferencesState.paymentsHistory,
                        this.paymentPreferencesHttpService.fetchPaymentHistory().pipe(
                            timeout(environment.TIMEOUT.apiCallDuration.paymentHistory),
                            catchError((error) => {
                                this.logger.error('PAYMENT HISTORY API ERROR: CANCEL AUTOMATIC PAYMENTS LANDING FALLBACK TO NULL', { error });
                                return of(null);
                            })
                        ))
                })
            ));
    }

    addSavedRefundMethodLanding(): Observable<returnTypes.AddSavedRefundPaymentMethodLanding> {
        return this.initializeSessionBeforeCalls().pipe(
            mergeMap(() => forkJoin({
                internetRegistrationResponse: this.retrieveInternetRegistrationResponse(),
                retrieveCustomerAgreement: this.httpCachingService.fetchRetrieveCustomerAgreementResponse(),
                savedBankAccountMethods: this.populateObservable(
                    this.paymentPreferencesState.savedPaymentMethods,
                    this.paymentPreferencesHttpService.fetchSavedPaymentMethods()
                ).pipe(
                    map((savedPaymentMethods) => savedPaymentMethods.filter((method) => method.electronicFundsTransfer !== undefined))
                )
            })
            ));
    }

    removeSavedRefundMethodLanding(): Observable<returnTypes.RemoveSavedRefundMethodLanding> {
        return this.initializeSessionBeforeCalls().pipe(
            mergeMap(() => forkJoin({
                savedRefundMethod: this.populateObservable(
                    this.paymentPreferencesState.refundMethod,
                    this.paymentPreferencesHttpService.fetchSavedRefundMethod()),
                internetRegistrationResponse: this.retrieveInternetRegistrationResponse(),
                retrieveCustomerAgreement: this.httpCachingService.fetchRetrieveCustomerAgreementResponse()
            })
            ));
    }

    initializeSessionBeforeCalls(): Observable<void> {
        return this.session.hasInitialized ? of(undefined) :
            this.httpCachingService.fetchRetrieveCustomerAgreementResponse().pipe(map(() => undefined));
    }

    initSessions(): Observable<void> {
        return this.initializeSessionBeforeCalls();
    }

    getChangeDueDateBillingDetails(): Observable<returnTypes.BillingDetails> {
        return this.initializeSessionBeforeCalls().pipe(
            mergeMap(() =>
                forkJoin({
                    retrieveCustomerAgreement: this.httpCachingService.fetchRetrieveCustomerAgreementResponse(),
                    bills: this.populateObservable(
                        this.paymentPreferencesState.bills,
                        this.paymentPreferencesHttpService.fetchBills())
                })
            ));
    }

    private populateObservable<T>(stateObjectToCheck: T, httpCallToMake: Observable<T>): Observable<T> {
        let outputObservable: Observable<T>;

        if (stateObjectToCheck) {
            outputObservable = of(stateObjectToCheck);
        } else {
            outputObservable = httpCallToMake;
        }

        return outputObservable;
    }
}
